import React from 'react';
import { useChangeAll } from './ChangeAllContext';
import {
  LEG_LOCATION_BULK_FIELDS,
  isBulkTargetLeg,
  type LegLocationBulkKey,
  type LegPointers,
} from './legLocationChangeAll';
import { useCanChangeAllLegLocation } from '../useCanChangeAllLegLocation';
import type { OrderLeg } from '../../../services/orders.service';

/**
 * "Change All" for a leg LOCATION — the pickup origin or the terminate destination.
 *
 * Renders a bare checkbox and WRAPS THE FIELD ITSELF in an outline, rather than
 * labelling the checkbox. In a dense grid of leg cells a tick with text beside it
 * still leaves the question "which of these does it mean?", and the answer is a
 * property of the field, not of the words. Outlining the field answers it directly:
 * the box belongs to the thing lit up next to it.
 *
 * The outline deepens to a filled highlight once armed, so "this field is offered"
 * and "this field is going out to every pro" read differently at a glance. It
 * borrows the ring+background vocabulary `LegLocationCell` already uses for its
 * stale state, so the grid has one visual language for "look at this cell".
 *
 * IT IS ONLY DRAWN ON THE LEG THAT WILL ACTUALLY BE WRITTEN — the leg the order's
 * `originateleg`/`terminateleg` pointer names, not a leg that merely looks like a
 * pickup. Drawing it anywhere else would promise a write somewhere other than where
 * the user is looking.
 *
 * Four gates, all of which must pass:
 *   - change-all mode is on, against ORDERS (a template has no live legs and its own
 *     field policy);
 *   - this leg is the pointed-at one;
 *   - `editable` — the very expression that decides whether this cell renders as an
 *     input, so the checkbox can never outlive the cell's own lock. Ticking alone is
 *     enough to WRITE, so a live checkbox beside a locked cell would not be locked
 *     at all;
 *   - the user holds both `leg-editing` and `order-lc-fields` at Edit.
 *
 * When any gate fails it renders `children` untouched — same DOM as before this
 * feature existed, so the grid outside change-all mode is byte-for-byte unchanged.
 */
export interface LegLocationBulkCheckboxProps {
  fieldKey: LegLocationBulkKey;
  leg: Pick<OrderLeg, 'legnum'>;
  pointers: LegPointers;
  /** May the user edit this cell on THIS leg right now (gates.canOrig/canDest)? */
  editable: boolean;
  /** The location field this flag refers to. Wrapped, not replaced. */
  children: React.ReactNode;
}

/**
 * Split in two so the PERMISSION lookup happens only once the cheap gates have
 * already passed.
 *
 * Hooks cannot be called conditionally, so a single component would resolve two
 * component permissions on every location cell of every leg on every render —
 * change-all off, on a template, anywhere. That is wasted work in the normal case
 * and, less obviously, it makes a permission provider a hard requirement of the
 * legs grid: every test that renders LegsGrid would have to mock
 * `useResolvedAccessLevel` whether or not it cares about Change All.
 */
const LegLocationBulkCheckbox: React.FC<LegLocationBulkCheckboxProps> = (props) => {
  const { enabled, isTemplate } = useChangeAll();
  if (!enabled || isTemplate) return <>{props.children}</>;
  if (!props.editable) return <>{props.children}</>;
  if (!isBulkTargetLeg(props.fieldKey, props.leg, props.pointers)) {
    return <>{props.children}</>;
  }
  return <PermittedLegLocationBulkCheckbox {...props} />;
};

const PermittedLegLocationBulkCheckbox: React.FC<LegLocationBulkCheckboxProps> = ({
  fieldKey,
  children,
}) => {
  const { checkedLegFields, toggleLegField } = useChangeAll();
  const permitted = useCanChangeAllLegLocation();
  if (!permitted) return <>{children}</>;

  const checked = checkedLegFields.has(fieldKey);
  // The visible affordance is an outline, which a screen reader cannot see — so the
  // accessible name has to carry what the outline conveys: WHICH field this is.
  const label = `Change All — ${LEG_LOCATION_BULK_FIELDS[fieldKey].label}`;
  return (
    <>
      <input
        type="checkbox"
        data-testid={`leg-bulk-${fieldKey}`}
        title="Change All"
        aria-label={label}
        checked={checked}
        onChange={() => toggleLegField(fieldKey)}
        className="h-3.5 w-3.5 shrink-0 accent-blue-600 cursor-pointer"
      />
      {/* The field this checkbox refers to. `flex-1 min-w-0` keeps the slot the
          location cell held as a direct flex child of DataCell, so arming the flag
          cannot reflow the row.

          BOTH states are highlighted, not just the armed one. The offered state is
          the one doing the explaining — it is what tells the user which field the
          box beside it means — so a hairline ring was too quiet to do that job in a
          grid this dense. It gets a full ring and a tint; arming deepens both. */}
      <span
        data-testid={`leg-bulk-field-${fieldKey}`}
        title="Change All"
        className={`flex min-w-0 flex-1 items-center rounded transition-colors ${
          checked
            ? 'ring-2 ring-blue-600 bg-blue-200 dark:bg-blue-800/60'
            : 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-950/50'
        }`}
      >
        {children}
      </span>
    </>
  );
};

export default LegLocationBulkCheckbox;
